/**
 * The network, as a festival field sees it.
 *
 * The pilot's lesson (sample/ozora-2026/sw.js): on weak festival wifi a
 * request neither succeeds nor fails — it HANGS. So nothing the app needs to
 * render may wait on one without a clock. The service worker races the app
 * shell; this is the same race for the API, which is cross-origin
 * (`api.<domain>`) and so never passes through the worker's shell strategy.
 */

/** The pilot's number, arrived at by a real failure at a real festival. */
export const NETWORK_TIMEOUT_MS = 2500;

export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`no answer in ${ms} ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Settle with `work`, or reject with `TimeoutError` after `ms` — whichever is
 * first. `work` is NOT cancelled: it keeps running, and whatever it does when
 * it lands (store a newer bundle, remember the tenant) still happens.
 */
export function withTimeout<T>(work: Promise<T>, ms = NETWORK_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError(ms)), ms);
    work.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

interface NetworkInformationLike extends EventTarget {
  saveData?: boolean;
  type?: string;
  effectiveType?: string;
}

function connection(): NetworkInformationLike | undefined {
  return (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
}

/**
 * Should a big download wait? True when the person asked their browser to save
 * data, or the Network Information API says the link is cellular or very slow.
 *
 * Unknown is NOT metered: most browsers (Safari, Firefox) expose no API at
 * all, and a festival app that never downloads photos on an iPhone would be
 * a worse failure than one that downloads them on a data plan.
 */
export function isDataSaving(): boolean {
  const c = connection();
  if (!c) return false;
  if (c.saveData) return true;
  // NOT `type === 'cellular'` (the user, 2026-10-07: "the download is
  // automatic when opening a rave"). At a festival mobile data IS the network,
  // and waiting for Wi-Fi meant a "Download now" button nobody expected. Data
  // Saver is the person's own choice, and 2G cannot carry it — those wait.
  return c.effectiveType === 'slow-2g' || c.effectiveType === '2g';
}

/** Call `cb` when the connection type changes (e.g. the phone joins Wi-Fi). */
export function onConnectionChange(cb: () => void): () => void {
  const c = connection();
  if (!c?.addEventListener) return () => {};
  c.addEventListener('change', cb);
  return () => c.removeEventListener('change', cb);
}

/**
 * How long one sync request (a PATCH of queued saves, a GET of pins) may take
 * before the drain gives up on it and backs off. Longer than the 2.5s race —
 * nothing is waiting on screen, and a batch is worth a slow answer — but
 * finite: a request that hangs for good used to leave `syncing` on for good.
 * The request itself is not cancelled; if it lands, the server has merged it,
 * and the next drain's resend is a no-op (per-record merge is idempotent).
 */
export const SYNC_TIMEOUT_MS = 15_000;
